"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { Suspense, useState } from "react";
import { Button } from "@/components/ui/button";

/**
 * Resolve the post-login redirect target from a `callbackUrl` query param.
 * Only same-origin relative paths are honoured (must start with a single "/").
 * Anything else (absolute URLs, protocol-relative "//host", missing) falls
 * back to the dashboard, preventing open-redirect to external origins.
 */
function safeCallbackUrl(callbackUrl: string | null): string {
	if (callbackUrl?.startsWith("/") && !callbackUrl.startsWith("//")) {
		return callbackUrl;
	}
	return "/dashboard";
}

function LoginForm() {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const router = useRouter();
	const searchParams = useSearchParams();

	// 2FA state
	const [step, setStep] = useState<"credentials" | "2fa">("credentials");
	const [twoFactorToken, setTwoFactorToken] = useState("");
	const [totpCode, setTotpCode] = useState("");
	const [useRecoveryCode, setUseRecoveryCode] = useState(false);
	const [recoveryCode, setRecoveryCode] = useState("");

	const doSignIn = async (verifiedTwoFactorToken?: string) => {
		const result = await signIn("credentials", {
			username,
			password,
			twoFactorToken: verifiedTwoFactorToken ?? "",
			redirect: false,
		});

		if (result?.error) {
			setError("Invalid username or password");
			setIsLoading(false);
		} else if (result?.ok) {
			router.push(safeCallbackUrl(searchParams.get("callbackUrl")));
			router.refresh();
		} else {
			setError("Sign in failed. Please try again.");
			setIsLoading(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);

		try {
			// First, validate credentials and check if password change is required
			const validateResponse = await fetch("/api/credentials/validate", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ username, password }),
			});

			const validateData = await validateResponse.json();

			if (!validateResponse.ok) {
				setError(validateData.error || "Invalid username or password");
				setIsLoading(false);
				return;
			}

			// If password change is required, redirect to change password page
			if (validateData.mustChangePassword) {
				router.push(`/change-password?token=${validateData.token}`);
				return;
			}

			// If 2FA is required, move to 2FA step
			if (validateData.requires2FA) {
				setTwoFactorToken(validateData.twoFactorToken);
				setStep("2fa");
				setIsLoading(false);
				return;
			}

			// Credentials valid and no password change or 2FA needed - proceed with login
			await doSignIn();
		} catch {
			setError("An error occurred. Please try again.");
			setIsLoading(false);
		}
	};

	const handleTwoFactorSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);

		try {
			const body = useRecoveryCode
				? { twoFactorToken, recoveryCode }
				: { twoFactorToken, code: totpCode };

			const verifyResponse = await fetch("/api/auth/2fa/login-verify", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			});

			const verifyData = await verifyResponse.json();

			if (!verifyResponse.ok) {
				setError(verifyData.error || verifyData.message || "Invalid code");
				setIsLoading(false);
				return;
			}

			// 2FA verified - proceed with sign in using the verified token
			await doSignIn(twoFactorToken);
		} catch {
			setError("An error occurred. Please try again.");
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
			{/* Animated Background Orbs */}
			<div className="fixed inset-0 overflow-hidden pointer-events-none">
				<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--brand-primary)]/20 rounded-full blur-3xl animate-pulse" />
				<div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[var(--brand-accent)]/15 rounded-full blur-3xl animate-pulse delay-1000" />
			</div>

			{/* Login Form */}
			<div className="relative w-full max-w-md">
				<div className="glass-panel p-8">
					{step === "credentials" && (
						<>
							<h1 className="text-[var(--foreground)] text-2xl font-bold mb-2">
								Welcome back
							</h1>
							<p className="text-[var(--foreground-muted)] mb-8">
								Sign in to your account
							</p>

							<form onSubmit={handleSubmit} className="space-y-6">
								<div>
									<label
										htmlFor="username"
										className="block text-sm font-medium text-[var(--foreground-muted)] mb-2"
									>
										Username
									</label>
									<input
										id="username"
										type="text"
										value={username}
										onChange={(e) => setUsername(e.target.value)}
										required
										className="w-full glass-input px-4 py-3 text-[var(--foreground)] placeholder:text-[var(--glass-text-ghost)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/50 focus:border-[var(--brand-primary)]/50"
										placeholder="john.doe"
									/>
								</div>

								<div>
									<label
										htmlFor="password"
										className="block text-sm font-medium text-[var(--foreground-muted)] mb-2"
									>
										Password
									</label>
									<input
										id="password"
										type="password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										required
										className="w-full glass-input px-4 py-3 text-[var(--foreground)] placeholder:text-[var(--glass-text-ghost)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/50 focus:border-[var(--brand-primary)]/50"
										placeholder="Enter your password"
									/>
								</div>

								{error && (
									<div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
										{error}
									</div>
								)}

								<Button
									type="submit"
									disabled={isLoading}
									className="w-full"
									size="lg"
								>
									{isLoading ? "Signing in..." : "Sign in"}
								</Button>
							</form>
						</>
					)}

					{step === "2fa" && (
						<>
							<h1 className="text-[var(--foreground)] text-2xl font-bold mb-2">
								Two-factor authentication
							</h1>
							<p className="text-[var(--foreground-muted)] mb-8">
								{useRecoveryCode
									? "Enter a recovery code"
									: "Enter the 6-digit code from your authenticator app"}
							</p>

							<form onSubmit={handleTwoFactorSubmit} className="space-y-6">
								<div>
									<label
										htmlFor="twoFactorInput"
										className="block text-sm font-medium text-[var(--foreground-muted)] mb-2"
									>
										{useRecoveryCode ? "Recovery code" : "Authenticator code"}
									</label>
									{useRecoveryCode ? (
										<input
											id="twoFactorInput"
											key="recovery"
											type="text"
											value={recoveryCode}
											onChange={(e) => setRecoveryCode(e.target.value)}
											required
											autoComplete="off"
											className="w-full glass-input px-4 py-3 text-[var(--foreground)] placeholder:text-[var(--glass-text-ghost)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/50 focus:border-[var(--brand-primary)]/50"
											placeholder="Enter recovery code"
										/>
									) : (
										<input
											id="twoFactorInput"
											key="totp"
											type="text"
											inputMode="numeric"
											pattern="[0-9]*"
											maxLength={6}
											value={totpCode}
											onChange={(e) =>
												setTotpCode(e.target.value.replace(/\D/g, ""))
											}
											required
											autoComplete="one-time-code"
											className="w-full glass-input px-4 py-3 text-[var(--foreground)] placeholder:text-[var(--glass-text-ghost)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/50 focus:border-[var(--brand-primary)]/50 tracking-widest text-center text-lg"
											placeholder="000000"
										/>
									)}
								</div>

								<button
									type="button"
									onClick={() => {
										setUseRecoveryCode((prev) => !prev);
										setTotpCode("");
										setRecoveryCode("");
										setError(null);
									}}
									className="text-sm text-[var(--brand-primary)] hover:underline focus:outline-none"
								>
									{useRecoveryCode
										? "Use authenticator code instead"
										: "Use recovery code instead"}
								</button>

								{error && (
									<div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
										{error}
									</div>
								)}

								<Button
									type="submit"
									disabled={isLoading}
									className="w-full"
									size="lg"
								>
									{isLoading ? "Verifying..." : "Verify"}
								</Button>
							</form>
						</>
					)}
				</div>

				{/* Footer */}
				<div className="mt-6 text-center">
					<div className="h-px bg-gradient-to-r from-transparent via-[var(--glass-separator)] to-transparent mb-4" />
					<p className="text-xs text-[var(--glass-text-faint)]">
						Powered by{" "}
						<span className="font-semibold text-[var(--brand-primary)]">
							cut
						</span>
						<span className="font-semibold text-[var(--foreground)]">my</span>
					</p>
				</div>
			</div>
		</div>
	);
}

// useSearchParams() forces a client-side bailout; without a Suspense boundary
// the production prerender of /login fails outright.
export default function LoginPage() {
	return (
		<Suspense fallback={null}>
			<LoginForm />
		</Suspense>
	);
}
